import type { MouseEventHandler, ReactNode } from 'react';
import { Children, useCallback, useEffect, useId, useState } from 'react';

import classNames from 'classnames';
import fastdom from 'fastdom';

import styles from './Gallery.module.css';

import { useHtmlElement } from '../../hooks';
import { useSlideshow } from '../../hooks/useSlideshow';
import { Chevron } from '../../icons';
import { IndexButton } from '../IndexButton';
import { ScrollSnap } from '../ScrollSnap';
import { StatusMessage } from '../StatusMessage';
import { Text } from '../Text';
import { Slide } from './Slide';
import { getIsActiveSlide } from './getIsActiveSlide';

/** Which contrast mode to use */
export type GalleryMode = 'light' | 'dark' | 'darker';

export interface GalleryProps {
  id?: string;
  /** The label used for this gallery. Required for accessibility. */
  label: string;
  /** Boolean value to toggle the display of index dots indicators */
  withDots?: boolean;
  /** Callback when the currently active slide is clicked */
  onActiveSlideClick?: MouseEventHandler;
  /** Callback when the current slide changes from a touch gestures */
  onSlideChange?: (nextIndex: number) => void;
  /** The currently active index when used in a controlled state pattern */
  activeIndex?: number;
  /** Content to render beneath the active slide */
  footnote?: ReactNode;
  /** Which contrast mode to use */
  mode?: GalleryMode;
  /** Whether to toggle the wide view */
  isWide?: boolean;
  /** Whether to toggle the debug code to visualize the intersections */
  isDebug?: boolean;
  /** Optional class name */
  className?: string;
  children?: ReactNode;
}

const initialRatios: number[] = [];

/**
 * Render a gallery component. This component accepts any number of children
 * that will each be rendered as a slide for the gallery.
 *
 * @see https://corgi-x.tfd.engineering/components/gallery
 */
export function Gallery({
  id: idFromProps,
  mode = 'dark',
  label,
  onSlideChange,
  withDots = false,
  onActiveSlideClick,
  footnote,
  activeIndex: activeIndexProp,
  isWide = false,
  className,
  isDebug = false,
  children,
}: GalleryProps) {
  const reactId = useId();
  const id = idFromProps || reactId;

  const messageReactId = useId();
  const messageId = idFromProps ? idFromProps + 'message' : messageReactId;

  const numberOfSlides = Children.count(children);
  const [containerEl, containerRef] = useHtmlElement();
  const [intersectionRatios, setIntersectionRatios] = useState(initialRatios);

  const { activeIndex, goToSlide } = useSlideshow({
    scrollableEl: containerEl,
    scrollBehavior: 'smooth',
    getIsActiveSlide,
    onSlideChange,
    activeIndex: activeIndexProp,
  });

  const handleScroll = useCallback((): void => {
    let nextIntersectionRatios: number[];

    fastdom.measure(() => {
      if (!containerEl) {
        return;
      }

      const scrollableRect = containerEl.getBoundingClientRect();
      const scrollableLeft = scrollableRect.left;
      const scrollableRight = scrollableRect.right;
      const scrollableWidth = scrollableRect.width;
      const slides = Array.from(containerEl.children);

      nextIntersectionRatios = slides.map(slide => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.width / 2 + slideRect.left;
        const animationReferenceRight = slideCenter + scrollableRect.width / 2;
        const animationReferenceLeft = slideCenter - scrollableRect.width / 2;

        const isIntersectingLeft =
          animationReferenceLeft <= scrollableLeft &&
          animationReferenceRight > scrollableLeft;

        const isIntersectingRight =
          animationReferenceRight >= scrollableRight &&
          animationReferenceLeft < scrollableRight;

        if (isIntersectingRight) {
          return (
            (scrollableWidth - (animationReferenceRight - scrollableRight)) /
            scrollableWidth
          );
        }

        if (isIntersectingLeft) {
          return (animationReferenceRight - scrollableLeft) / scrollableWidth;
        }

        return 0;
      });

      fastdom.mutate(() => {
        setIntersectionRatios(nextIntersectionRatios);
      });
    });
  }, [containerEl]);

  // Set up initial positions when element is rendered
  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  return (
    <section
      aria-label={label}
      className={classNames(className, {
        [styles['is-wide']]: isWide,
        [styles['is-not-wide']]: !isWide,
      })}
      data-active-slide={activeIndex}
    >
      <div className={styles.container}>
        {isWide && (
          <>
            <button
              onClick={(): void => goToSlide(activeIndex - 1)}
              className={classNames(styles['arrow-button'])}
              disabled={activeIndex === 0}
              aria-label="Previous Slide"
            >
              <Chevron
                fill={mode === 'light' ? 'white' : 'kale-3'}
                size={72}
                orientation={'left'}
              />
            </button>
            <button
              onClick={(): void => goToSlide(activeIndex + 1)}
              className={classNames(
                styles['arrow-button'],
                styles['forward-button']
              )}
              disabled={activeIndex === numberOfSlides - 1}
              aria-label="Next Slide"
            >
              <Chevron fill={mode === 'light' ? 'white' : 'kale-3'} size={72} />
            </button>
          </>
        )}
        <ScrollSnap
          as="ul"
          id={id}
          containerRef={containerRef}
          className={styles.horizontalSnap}
          onScroll={handleScroll}
        >
          {Children.map(children, (child, index) => {
            const handleClick =
              index === activeIndex
                ? onActiveSlideClick
                : (): void => goToSlide(index);

            return (
              <Slide
                aria-label={`${index + 1} of ${numberOfSlides}`}
                aria-describedby={messageId}
                onClick={handleClick}
                isDebug={isDebug}
                isWide={isWide}
                index={index}
                intersectionRatio={intersectionRatios[index]}
                active={index === activeIndex}
              >
                {child}
              </Slide>
            );
          })}
        </ScrollSnap>
      </div>
      <StatusMessage
        politeness="polite"
        id={messageId}
        className={styles.message}
      >
        <Text color={['dark', 'darker'].includes(mode) ? 'Kale3' : 'White'}>
          {footnote}
        </Text>
      </StatusMessage>
      {withDots && (
        <div className={styles.dots}>
          {Children.map(children, (_, childIndex) => {
            return (
              <IndexButton
                variant={mode}
                aria-label={`Go to slide ${childIndex}`}
                aria-controls={id}
                active={childIndex === activeIndex}
                onClick={(): void => goToSlide(childIndex)}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
